import { useEffect } from "react";
import { SEO } from "@/lib/seo";
import { CompareBar } from "./CompareBar";
import "@/styles/brand-a.css";

/**
 * Brand A — PulseBoard landing page built to the editorial public-landing
 * direction. Source Serif 4 editorial serif + Geist sans + Geist Mono,
 * warm clinical canvas, explicit 5-tier severity scale, editorial
 * positioning.
 *
 * Everything scoped under `.brand-a` via brand-a.css so the tokens
 * don't leak into the rest of the app.
 *
 * Design calibration (taste-skill dials):
 *   DESIGN_VARIANCE=3 — editorial symmetry, not Dribbble asymmetry
 *   MOTION_INTENSITY=3 — CSS hover transforms only; no spring physics
 *   VISUAL_DENSITY=4 — newspaper-weight, not cockpit
 */

const PulseMark = ({ size = 24 }: { size?: number }) => (
  <svg viewBox="0 0 28 28" width={size} height={size} aria-hidden="true">
    <rect x="2" y="2" width="24" height="24" rx="6" fill="#1F8A8A" />
    <path
      d="M7 15 L11 15 L12.5 11 L14.5 19 L16 15 L21 15"
      stroke="#F4EFE4"
      strokeWidth="1.7"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const HeroMark = () => (
  <svg viewBox="0 0 320 320" style={{ width: "100%", maxWidth: 420 }} aria-hidden="true">
    <defs>
      <linearGradient id="heroWashA" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#1F8A8A" />
        <stop offset="1" stopColor="#176B6B" />
      </linearGradient>
    </defs>
    <rect x="30" y="30" width="260" height="260" rx="56" fill="url(#heroWashA)" />
    <path
      d="M70 170 L115 170 L132 128 L155 212 L172 170 L250 170"
      stroke="#F4EFE4"
      strokeWidth="12"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="250" cy="170" r="6" fill="#F27E62" />
  </svg>
);

export default function LandingBrandA() {
  // Load the Google Fonts once for this variant.
  useEffect(() => {
    const id = "brand-a-fonts";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700&family=Geist+Mono:wght@400;500&family=Source+Serif+4:ital,opsz,wght@0,8..60,300;0,8..60,400;0,8..60,500;0,8..60,600;1,8..60,400&display=swap";
    document.head.appendChild(link);
  }, []);

  return (
    <>
      <CompareBar />
      <SEO
        title="PulseBoard — The noticeboard Australian GPs open first"
        description="A clinician-first workplace portal — public health alerts, specialist availability, clinical tools, and referral transport on one calm surface."
        path="/compare/brand-a"
        noindex
      />

      <div className="brand-a" style={{ minHeight: "100dvh" }}>
        {/* ── Nav ──────────────────────────────────────────── */}
        <nav className="ba-nav" aria-label="Primary navigation">
          <div className="ba-nav__brand">
            <PulseMark size={22} />
            <span>Pulseboard</span>
            <span className="ba-nav__tag">Canberra · A.C.T.</span>
          </div>
          <ul className="ba-nav__links">
            <li><a href="#product">Product</a></li>
            <li><a href="#pillars">Pillars</a></li>
            <li><a href="#morning">A morning</a></li>
            <li><a href="#severity">Severity</a></li>
            <li><a href="#voice">Voice</a></li>
          </ul>
          <div style={{ display: "flex", gap: 8 }}>
            <a href="#signup" className="ba-btn ba-btn--secondary ba-btn--sm">
              Sign in
            </a>
            <a href="#signup" className="ba-btn ba-btn--primary ba-btn--sm">
              Request access
            </a>
          </div>
        </nav>

        {/* ── Hero ─────────────────────────────────────────── */}
        <header style={{ padding: "80px 40px 100px", borderBottom: "1px solid rgba(15,42,42,0.08)" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1.3fr 1fr",
              gap: 80,
              maxWidth: 1400,
              margin: "0 auto",
              alignItems: "center",
            }}
            className="ba-hero-grid"
          >
            <div>
              <div
                className="ba-eyebrow"
                style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 32 }}
              >
                <span className="ba-dot" />
                <span>Clinician-first workplace portal</span>
                <span style={{ color: "var(--ba-parch-2)" }}>—</span>
                <span>Canberra pilot · April 2026</span>
              </div>

              <h1
                className="ba-display"
                style={{ fontSize: "clamp(48px, 6vw, 84px)", marginBottom: 32 }}
              >
                The noticeboard<br />
                Australian&nbsp;GPs<br />
                <em>open first.</em>
              </h1>

              <p className="ba-lede" style={{ maxWidth: 560, marginBottom: 40 }}>
                PulseBoard collapses the five-tab morning ritual — ACT Health, TGA
                recalls, RACGP guidelines, specialist availability, referral status —
                onto one calm surface so the next patient gets a clinician who is
                informed, not overloaded.
              </p>

              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <a href="#signup" className="ba-btn ba-btn--primary">
                  Request Canberra pilot access
                </a>
                <a href="#morning" className="ba-btn ba-btn--secondary">
                  See a morning on PulseBoard
                </a>
              </div>

              <dl
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "16px 40px",
                  maxWidth: 520,
                  marginTop: 56,
                  paddingTop: 24,
                  borderTop: "1px solid rgba(15,42,42,0.12)",
                }}
              >
                {[
                  ["Status", "Canberra pilot — invite only"],
                  ["Region", "GCP australia-southeast1"],
                  ["Data residency", "Australia, always"],
                  ["Compliance", "AHPRA · RACGP · Privacy Act"],
                ].map(([k, v]) => (
                  <div key={k}>
                    <dt className="ba-eyebrow" style={{ marginBottom: 4 }}>{k}</dt>
                    <dd style={{ margin: 0, fontSize: 15, fontWeight: 500 }}>{v}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div style={{ display: "grid", placeItems: "center" }} aria-hidden>
              <div
                style={{
                  position: "relative",
                  padding: 40,
                  background: "var(--ba-bone-50)",
                  border: "1px solid rgba(15,42,42,0.08)",
                  borderRadius: "var(--ba-r-lg)",
                  boxShadow: "var(--ba-shadow-2)",
                }}
              >
                <HeroMark />
                <div
                  style={{
                    position: "absolute",
                    bottom: 16,
                    left: 16,
                    right: 16,
                    display: "flex",
                    justifyContent: "space-between",
                    fontFamily: "var(--ba-font-mono)",
                    fontSize: 10,
                    letterSpacing: 1.5,
                    textTransform: "uppercase",
                    color: "var(--ba-stone-7)",
                  }}
                >
                  <span>The Mark</span>
                  <span>Noticeboard + one tick</span>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* ── Positioning ──────────────────────────────────── */}
        <section id="product" style={{ padding: "72px 40px", maxWidth: 1400, margin: "0 auto" }}>
          <div className="ba-positioning">
            <div className="ba-positioning__label">Positioning statement</div>
            <blockquote className="ba-positioning__quote">
              For Australian GPs, <span className="ba-hl">PulseBoard</span> is the{" "}
              <span className="ba-hl">first URL of the day</span> — a trusted noticeboard
              that gathers public-health alerts, specialist availability, and daily clinical
              tools onto one calm surface, so the next patient gets a clinician who is
              informed, not overloaded.
            </blockquote>
          </div>
        </section>

        {/* ── Six Pillars ──────────────────────────────────── */}
        <section
          id="pillars"
          style={{ padding: "40px 40px 88px", maxWidth: 1400, margin: "0 auto" }}
        >
          <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 28, marginBottom: 40 }}>
            <span className="ba-eyebrow" style={{ paddingTop: 8 }}>01 · Six pillars</span>
            <div>
              <h2 className="ba-section-title">What sits on the board.</h2>
              <p className="ba-lede" style={{ maxWidth: 660, marginTop: 10 }}>
                One surface, six daily functions. Clinical content stays free and unbiased;
                specialists and vendors pay for placement, on a visibly separate background.
              </p>
            </div>
          </div>

          <div className="ba-pillars">
            <article className="ba-pillar ba-pillar--wide">
              <div className="ba-pillar__num">i · Public health feed</div>
              <h3 className="ba-pillar__title">Alerts you'd have read anyway, minus the five tabs.</h3>
              <p className="ba-pillar__body">
                ACT Health, TGA, RACGP, AIHW, BOM ACT, ACT ESA — curated by PulseBoard
                moderators into a single feed with 7 categories × 5 severities. Read, action,
                or defer. The morning hook.
              </p>
            </article>

            <article className="ba-pillar ba-pillar--reg">
              <div className="ba-pillar__num">ii · Toolbox</div>
              <h3 className="ba-pillar__title">12 RACGP-aligned tools at the top of the day.</h3>
              <p className="ba-pillar__body">
                AUSDRISK, FRAX, cancer screening, immunisation tracker — ready when you
                need them, not buried behind a search.
              </p>
            </article>

            <article className="ba-pillar ba-pillar--third">
              <div className="ba-pillar__num">iii · Directory</div>
              <h3 className="ba-pillar__title">Live specialist availability.</h3>
              <p className="ba-pillar__body">
                Google Calendar Watch auto-populates windows. No specialist login for routine
                updates.
              </p>
            </article>

            <article className="ba-pillar ba-pillar--third">
              <div className="ba-pillar__num">iv · Referrals</div>
              <h3 className="ba-pillar__title">Vertex Gemini drafts. GPs review.</h3>
              <p className="ba-pillar__body">
                Every AI draft carries a visible "GP must review" banner. Payload KMS-encrypted
                and opt-in; metadata-only by default.
              </p>
            </article>

            <article className="ba-pillar ba-pillar--third">
              <div className="ba-pillar__num">v · Forum</div>
              <h3 className="ba-pillar__title">Doctor-only, identity-checked.</h3>
              <p className="ba-pillar__body">
                Reddit-style threads, strict moderation, medicine outside marketing reach.
              </p>
            </article>

            <article className="ba-pillar ba-pillar--half">
              <div className="ba-pillar__num">vi a · Spotlight</div>
              <h3 className="ba-pillar__title">New-practitioner placements, AHPRA-compliant.</h3>
              <p className="ba-pillar__body">
                Rendered on a Parchment background — visibly separate from editorial alerts.
                "Sponsored placement" tag on every card.
              </p>
            </article>

            <article className="ba-pillar ba-pillar--half">
              <div className="ba-pillar__num">vi b · Vendor marketplace</div>
              <h3 className="ba-pillar__title">Fitout · MDO · accounting · non-medical only.</h3>
              <p className="ba-pillar__body">
                The line between clinical content and paid placement is visible, always. No
                pharma, no device manufacturers, no ambiguity.
              </p>
            </article>
          </div>
        </section>

        {/* ── Morning Brief Mock ──────────────────────────── */}
        <section
          id="morning"
          style={{
            background: "var(--ba-parch)",
            padding: "88px 40px",
            borderTop: "1px solid rgba(15,42,42,0.08)",
            borderBottom: "1px solid rgba(15,42,42,0.08)",
          }}
        >
          <div style={{ maxWidth: 1200, margin: "0 auto" }}>
            <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 28, marginBottom: 40 }}>
              <span className="ba-eyebrow" style={{ paddingTop: 8 }}>02 · A Saturday at 07:00</span>
              <div>
                <h2 className="ba-section-title">What the GP actually sees.</h2>
                <p className="ba-lede" style={{ maxWidth: 660, marginTop: 10 }}>
                  Good morning, 4 new alerts overnight, 1 marked urgent. No emojis. No
                  exclamation marks. Names, sources, severities, done.
                </p>
              </div>
            </div>

            <div className="ba-mock">
              <div className="ba-mock__chrome">
                <div className="ba-mock__dots">
                  <span /><span /><span />
                </div>
                <div className="ba-mock__url">pulseboard.clinicos.com.au/canberra</div>
              </div>
              <div className="ba-mock__body">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    paddingBottom: 14,
                    borderBottom: "1px solid rgba(15,42,42,0.08)",
                  }}
                >
                  <PulseMark size={22} />
                  <span style={{ fontWeight: 600 }}>Pulseboard</span>
                  <span
                    className="ba-mono ba-mono--dim"
                    style={{ fontSize: 11, paddingLeft: 12, borderLeft: "1px solid rgba(15,42,42,0.12)" }}
                  >
                    Canberra
                  </span>
                  <nav
                    style={{
                      display: "flex",
                      gap: 20,
                      marginLeft: 24,
                      fontSize: 13,
                      color: "var(--ba-stone-7)",
                    }}
                  >
                    <span style={{ color: "var(--ba-ink)", fontWeight: 500 }}>Feed</span>
                    <span>Toolbox</span>
                    <span>Directory</span>
                    <span>Referrals</span>
                    <span>Forum</span>
                  </nav>
                  <span
                    style={{
                      marginLeft: "auto",
                      fontSize: 13,
                      color: "var(--ba-stone-7)",
                    }}
                  >
                    Dr K. Reyes
                  </span>
                </div>

                <div className="ba-mock__greeting">
                  <div className="ba-mono ba-mono--dim" style={{ fontSize: 11, letterSpacing: 1.5, textTransform: "uppercase" }}>
                    Saturday · 18 April 2026 · 06:58
                  </div>
                  <h2>Good morning, Dr Reyes.</h2>
                  <p style={{ margin: 0, color: "var(--ba-stone-7)", maxWidth: 640 }}>
                    <strong>4 new alerts</strong> overnight — 1 urgent. <strong>3 referrals</strong>{" "}
                    awaiting specialist response. Your first appointment is at 08:30.
                  </p>
                </div>

                <div style={{ display: "grid", gap: 10 }}>
                  <article className="ba-alert ba-alert--l4 ba-alert--compact">
                    <div className="ba-alert__stripe" />
                    <div className="ba-alert__body">
                      <div className="ba-alert__meta">
                        <span className="ba-tag ba-tag--coral">L4 · Urgent</span>
                        <span className="ba-mono" style={{ fontSize: 11 }}>TGA/RECALL/2026-117</span>
                        <span className="ba-mono ba-mono--dim" style={{ fontSize: 11 }}>06:42 · ACT</span>
                      </div>
                      <h4 className="ba-alert__title">
                        Voluntary recall — metformin 500mg, batch 2025M7
                      </h4>
                    </div>
                  </article>

                  <article className="ba-alert ba-alert--l3 ba-alert--compact">
                    <div className="ba-alert__stripe" />
                    <div className="ba-alert__body">
                      <div className="ba-alert__meta">
                        <span className="ba-tag ba-tag--ochre">L3 · Caution</span>
                        <span className="ba-mono" style={{ fontSize: 11 }}>ACTESA/2026-0418-02</span>
                        <span className="ba-mono ba-mono--dim" style={{ fontSize: 11 }}>05:15 · Tuggeranong</span>
                      </div>
                      <h4 className="ba-alert__title">
                        Smoke haze — controlled burn south of Tuggeranong
                      </h4>
                    </div>
                  </article>

                  <article className="ba-alert ba-alert--l2 ba-alert--compact">
                    <div className="ba-alert__stripe" />
                    <div className="ba-alert__body">
                      <div className="ba-alert__meta">
                        <span className="ba-tag ba-tag--amber">L2 · Advisory</span>
                        <span className="ba-mono" style={{ fontSize: 11 }}>RACGP/2026-Q2-04</span>
                        <span className="ba-mono ba-mono--dim" style={{ fontSize: 11 }}>Yesterday · National</span>
                      </div>
                      <h4 className="ba-alert__title">
                        Updated cervical screening — self-collect pathway
                      </h4>
                    </div>
                  </article>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Severity scale ──────────────────────────────── */}
        <section id="severity" style={{ padding: "88px 40px", maxWidth: 1400, margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 28, marginBottom: 40 }}>
            <span className="ba-eyebrow" style={{ paddingTop: 8 }}>03 · Severity</span>
            <div>
              <h2 className="ba-section-title">A gradient of concern.</h2>
              <p className="ba-lede" style={{ maxWidth: 660, marginTop: 10 }}>
                We reject the traffic-light cliché. Severity escalates through chroma and
                hue warmth — info, advisory, caution, urgent, critical. Coral is reserved.
                Red, rarer still.
              </p>
            </div>
          </div>

          <div className="ba-severity">
            {[
              { l: "L1 · Info", bg: "#DDE4E4", fg: "#5A6E6E", d: "Context, background, non-urgent updates." },
              { l: "L2 · Advisory", bg: "#E8D9B8", fg: "#6B5518", d: "New guidance, non-critical changes, FYI." },
              { l: "L3 · Caution", bg: "#F0C999", fg: "#6E3D12", d: "Act soon. Review before next shift." },
              { l: "L4 · Urgent", bg: "#F27E62", fg: "#2A0F08", d: "Same-day action. Recalls, outbreak alerts." },
              { l: "L5 · Critical", bg: "#9C2B1E", fg: "#F4EFE4", d: "Immediate. Emergency response required." },
            ].map((s) => (
              <div key={s.l} className="ba-sev" style={{ background: s.bg, color: s.fg }}>
                <div className="ba-sev__label">{s.l}</div>
                <div className="ba-sev__title">{s.l.split(" · ")[1]}</div>
                <div className="ba-sev__desc">{s.d}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Voice examples ──────────────────────────────── */}
        <section
          id="voice"
          style={{
            background: "var(--ba-bone-50)",
            padding: "88px 40px",
            borderTop: "1px solid rgba(15,42,42,0.08)",
          }}
        >
          <div style={{ maxWidth: 1000, margin: "0 auto" }}>
            <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 28, marginBottom: 40 }}>
              <span className="ba-eyebrow" style={{ paddingTop: 8 }}>04 · Voice</span>
              <div>
                <h2 className="ba-section-title">On a Monday before coffee.</h2>
                <p className="ba-lede" style={{ maxWidth: 660, marginTop: 10 }}>
                  Specific. Plain-spoken. Deferential. Never breathless.
                </p>
              </div>
            </div>

            <div style={{ display: "grid", gap: 20 }}>
              {[
                {
                  no: "🎉 Exciting news! We've just launched AI-powered referrals that will revolutionise your workflow!",
                  yes: "Vertex Gemini can draft a referral from your notes. The GP reviews before send; every draft carries an AI-assisted banner. Opt in per referral.",
                },
                {
                  no: "Oops! Something went wrong. Please try again later 😬",
                  yes: "The ACT Health feed is offline. We last refreshed at 06:42. Your alert queue is unchanged; you'll see the next poll at 07:00.",
                },
                {
                  no: "Welcome to PulseBoard — your all-in-one solution for modern general practice!",
                  yes: "Good morning, Dr Reyes. 4 new alerts overnight — 1 TGA recall marked urgent. Waiting list last updated 05:15.",
                },
              ].map((ex, i) => (
                <div
                  key={i}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "120px 1fr",
                    gap: 24,
                    padding: "20px 0",
                    borderBottom:
                      i < 2 ? "1px solid rgba(15,42,42,0.08)" : "none",
                  }}
                >
                  <div>
                    <div className="ba-eyebrow" style={{ color: "var(--ba-coral-d)", marginBottom: 8 }}>
                      Not this
                    </div>
                    <div className="ba-eyebrow" style={{ color: "var(--ba-teal-600)" }}>
                      This
                    </div>
                  </div>
                  <div style={{ display: "grid", gap: 12 }}>
                    <p style={{
                      margin: 0, padding: 14,
                      background: "rgba(242,126,98,0.08)",
                      border: "1px solid rgba(242,126,98,0.2)",
                      borderRadius: "var(--ba-r)",
                      color: "var(--ba-stone-7)",
                      textDecoration: "line-through",
                      textDecorationColor: "rgba(193,90,66,0.5)",
                    }}>
                      {ex.no}
                    </p>
                    <p style={{
                      margin: 0, padding: 14,
                      background: "var(--ba-teal-50)",
                      border: "1px solid rgba(31,138,138,0.2)",
                      borderRadius: "var(--ba-r)",
                      color: "var(--ba-ink)",
                    }}>
                      {ex.yes}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Signup ───────────────────────────────────────── */}
        <section id="signup" style={{ padding: "100px 40px" }}>
          <div
            style={{
              maxWidth: 760,
              margin: "0 auto",
              textAlign: "center",
              padding: "56px 40px",
              background: "var(--ba-ink)",
              color: "var(--ba-bone)",
              borderRadius: "var(--ba-r-lg)",
            }}
          >
            <div
              className="ba-eyebrow"
              style={{ color: "rgba(244,239,228,0.6)", marginBottom: 18 }}
            >
              Canberra pilot — invite only
            </div>
            <h2
              className="ba-display"
              style={{ fontSize: "clamp(36px, 4vw, 56px)", marginBottom: 20, color: "var(--ba-bone)" }}
            >
              For the GP who opens<br />five tabs before coffee.
            </h2>
            <p style={{ color: "rgba(244,239,228,0.75)", fontSize: 18, lineHeight: 1.55, marginBottom: 32 }}>
              Request access for your practice. We'll reach out with onboarding details
              and the next pilot window.
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
              <a href="mailto:hello@pulseboard.clinicos.com.au?subject=Canberra%20pilot%20access"
                 className="ba-btn ba-btn--urgent">
                Request pilot access
              </a>
              <a href="/compare/brand-b" className="ba-btn ba-btn--ghost" style={{ color: "rgba(244,239,228,0.7)" }}>
                See Brand B
              </a>
            </div>
          </div>
        </section>

        {/* ── Footer ───────────────────────────────────────── */}
        <footer className="ba-foot">
          <div>© 2026 ClinicOS Pty Ltd · Canberra pilot</div>
          <div>PulseBoard · Clinical referral availability platform</div>
        </footer>
      </div>
    </>
  );
}
